// Source of truth for the `(attorneys, approvals, status, finalizedAt)`
// invariants. Type-only Prisma imports keep this module frontend-safe.
import { DocumentStatus } from "@prisma/client-generated";
import type { Prisma, PrismaClient } from "@prisma/client-generated";

type TxLike = PrismaClient | Prisma.TransactionClient;

// `(attorneys, approvals) → DocumentStatus`. 0 attorneys collapses to
// NOT_APPROVED regardless of any orphan approval timestamps.
export const calculateDocumentStatus = (
  attorneyAApprovedAt: Date | null,
  attorneyBApprovedAt: Date | null,
  attorneyAId: string | null,
  attorneyBId: string | null,
): DocumentStatus => {
  const approvedCount =
    (attorneyAApprovedAt ? 1 : 0) + (attorneyBApprovedAt ? 1 : 0);
  const hasTwoAttorneys = !!(attorneyAId && attorneyBId);
  const hasAnyAttorney = attorneyAId !== null || attorneyBId !== null;

  if (!hasAnyAttorney) return DocumentStatus.NOT_APPROVED;

  if (hasTwoAttorneys) {
    return approvedCount === 2
      ? DocumentStatus.FULLY_APPROVED
      : approvedCount === 1
        ? DocumentStatus.PARTIALLY_APPROVED
        : DocumentStatus.NOT_APPROVED;
  }
  return approvedCount === 1
    ? DocumentStatus.FULLY_APPROVED
    : DocumentStatus.NOT_APPROVED;
};

export interface DocumentApprovalAxes {
  attorneyAId: string | null;
  attorneyBId: string | null;
  attorneyAApprovedAt: Date | null;
  attorneyBApprovedAt: Date | null;
}

export interface DocumentApprovalPriorState extends DocumentApprovalAxes {
  status: DocumentStatus;
  finalizedAt: Date | null;
  finalizedAtPrior: Date | null;
}

export interface RecomputedApprovalState {
  status: DocumentStatus;
  statusModifiedAt: Date;
  finalizedAt: Date | null;
  finalizedAtPrior: Date | null;
}

// Pure recompute of `(status, statusModifiedAt, finalizedAt, finalizedAtPrior)`.
// Callers merge the return into their `tx.document.update` payload.
export const recomputeDocumentApprovalState = (
  prior: DocumentApprovalPriorState,
  next: DocumentApprovalAxes,
  now: Date = new Date(),
): RecomputedApprovalState => {
  const status = calculateDocumentStatus(
    next.attorneyAApprovedAt,
    next.attorneyBApprovedAt,
    next.attorneyAId,
    next.attorneyBId,
  );

  const wasFullyApproved = prior.status === DocumentStatus.FULLY_APPROVED;
  const isFullyApproved = status === DocumentStatus.FULLY_APPROVED;

  let finalizedAt = prior.finalizedAt;
  let finalizedAtPrior = prior.finalizedAtPrior;

  if (wasFullyApproved && !isFullyApproved) {
    finalizedAtPrior = finalizedAt;
    finalizedAt = null;
  } else if (!wasFullyApproved && isFullyApproved) {
    finalizedAt = now;
  }
  // FULLY↔FULLY and not-FULLY↔not-FULLY: leave finalizedAt as-is.
  return { status, statusModifiedAt: now, finalizedAt, finalizedAtPrior };
};

// Removes one side's attorney + approvals + `*ApprovedAtPrior` and reconciles
// status / finalizedAt. Returns `null` if the doc no longer exists.
export async function clearAttorneyForSide(params: {
  tx: TxLike;
  documentId: string;
  side: "A" | "B";
  updatedById: string;
  /** Extra fields to merge into the same update (e.g. `chat` reassignments). */
  extraData?: Prisma.DocumentUncheckedUpdateInput;
}): Promise<RecomputedApprovalState | null> {
  const { tx, documentId, side, updatedById, extraData } = params;
  const doc = await tx.document.findUnique({
    where: { id: documentId },
    select: {
      attorneyAId: true,
      attorneyBId: true,
      attorneyAApprovedAt: true,
      attorneyBApprovedAt: true,
      status: true,
      finalizedAt: true,
      finalizedAtPrior: true,
    },
  });
  if (!doc) return null;

  const sideClear =
    side === "A"
      ? {
          attorneyAId: null,
          attorneyAApprovedAt: null,
          attorneyAApprovedAtPrior: null,
        }
      : {
          attorneyBId: null,
          attorneyBApprovedAt: null,
          attorneyBApprovedAtPrior: null,
        };

  const next: DocumentApprovalAxes = {
    attorneyAId: side === "A" ? null : doc.attorneyAId,
    attorneyBId: side === "B" ? null : doc.attorneyBId,
    attorneyAApprovedAt: side === "A" ? null : doc.attorneyAApprovedAt,
    attorneyBApprovedAt: side === "B" ? null : doc.attorneyBApprovedAt,
  };

  const recomputed = recomputeDocumentApprovalState(doc, next);

  await tx.document.update({
    where: { id: documentId },
    data: {
      ...sideClear,
      ...recomputed,
      updatedById,
      ...extraData,
    },
  });
  return recomputed;
}

// Assigns an attorney to one side (no approval) and recomputes status /
// finalizedAt — a 1-attorney FULLY → 2-attorney PARTIAL transition matters.
//
// Defensively clears the assigned side's `attorneyXApprovedAt` /
// `attorneyXApprovedAtPrior`. Callers that null an attorney slot are expected
// to also null its approval (see `clearAttorneyForSide`), but enforcing it
// here too prevents a stale orphan timestamp from a future / unaudited write
// path from silently re-binding to the newly assigned attorney and finalizing
// the document without that attorney actually approving it.
export async function setAttorneyForSide(params: {
  tx: TxLike;
  documentId: string;
  side: "A" | "B";
  attorneyId: string;
  updatedById: string;
  extraData?: Prisma.DocumentUncheckedUpdateInput;
}): Promise<RecomputedApprovalState | null> {
  const { tx, documentId, side, attorneyId, updatedById, extraData } = params;
  const doc = await tx.document.findUnique({
    where: { id: documentId },
    select: {
      attorneyAId: true,
      attorneyBId: true,
      attorneyAApprovedAt: true,
      attorneyBApprovedAt: true,
      status: true,
      finalizedAt: true,
      finalizedAtPrior: true,
    },
  });
  if (!doc) return null;

  const sideAssign =
    side === "A"
      ? {
          attorneyAId: attorneyId,
          attorneyAApprovedAt: null,
          attorneyAApprovedAtPrior: null,
        }
      : {
          attorneyBId: attorneyId,
          attorneyBApprovedAt: null,
          attorneyBApprovedAtPrior: null,
        };

  const next: DocumentApprovalAxes = {
    attorneyAId: side === "A" ? attorneyId : doc.attorneyAId,
    attorneyBId: side === "B" ? attorneyId : doc.attorneyBId,
    attorneyAApprovedAt: side === "A" ? null : doc.attorneyAApprovedAt,
    attorneyBApprovedAt: side === "B" ? null : doc.attorneyBApprovedAt,
  };

  const recomputed = recomputeDocumentApprovalState(doc, next);

  await tx.document.update({
    where: { id: documentId },
    data: {
      ...sideAssign,
      ...recomputed,
      updatedById,
      ...extraData,
    },
  });
  return recomputed;
}
